import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Chip } from '@/components/ui/Chip';
import {
  ACTIVITY_GROUPS,
  ACTIVITY_TYPE_LABELS,
  type ActivityType,
  type ActivityGroup,
} from '@/types/activity';

interface GroupedActivitySelectProps {
  selected: ActivityType[];
  onChange: (types: ActivityType[]) => void;
  placeholder?: string;
}

const ALL_TYPES = ACTIVITY_GROUPS.flatMap((group) => group.types);

export function GroupedActivitySelect({
  selected,
  onChange,
  placeholder = 'Select activity types...',
}: GroupedActivitySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filteredGroups = useMemo(() => {
    if (!search) return ACTIVITY_GROUPS;
    const term = search.toLowerCase();
    return ACTIVITY_GROUPS.map((group) => ({
      ...group,
      types: group.types.filter((type) =>
        ACTIVITY_TYPE_LABELS[type].toLowerCase().includes(term)
      ),
    })).filter((group) => group.types.length > 0);
  }, [search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const toggleType = (type: ActivityType) => {
    if (selectedSet.has(type)) {
      onChange(selected.filter((t) => t !== type));
    } else {
      onChange([...selected, type]);
    }
  };

  const toggleGroup = (group: ActivityGroup) => {
    const allSelected = group.types.every((type) => selectedSet.has(type));
    if (allSelected) {
      onChange(selected.filter((type) => !group.types.includes(type)));
    } else {
      onChange(Array.from(new Set([...selected, ...group.types])));
    }
  };

  const selectAll = () => {
    if (selected.length === ALL_TYPES.length) {
      onChange([]);
    } else {
      onChange([...ALL_TYPES]);
    }
  };

  const clearAll = () => onChange([]);

  const displayedItems = selected.slice(0, 3);
  const remainingCount = selected.length - displayedItems.length;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex min-h-[42px] w-full items-center justify-between gap-2 rounded-lg border border-border bg-white px-3 py-2 text-left text-sm transition-all',
          'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          isOpen && 'border-primary ring-2 ring-primary/20'
        )}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {selected.length === 0 ? (
            <span className="text-muted">{placeholder}</span>
          ) : (
            <>
              {displayedItems.map((type) => (
                <Chip
                  key={type}
                  size="sm"
                  variant="selected"
                  removable
                  onRemove={() => toggleType(type)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {ACTIVITY_TYPE_LABELS[type]}
                </Chip>
              ))}
              {remainingCount > 0 && (
                <span className="text-sm text-muted">+{remainingCount} more</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selected.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="rounded p-1 text-muted hover:bg-gray-200 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 max-h-96 w-full overflow-hidden rounded-xl border border-border bg-white shadow-lg">
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search activity types..."
                className="w-full rounded-lg border border-border bg-gray-50 py-2 pl-10 pr-3 text-sm placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs text-muted">
            <span>{selected.length} selected</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs font-medium text-foreground hover:underline"
              >
                {selected.length === ALL_TYPES.length ? 'Clear all' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-medium text-foreground hover:underline"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-2">
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted">
                No results found
              </div>
            ) : (
              filteredGroups.map((group) => {
                const groupSelectedCount = group.types.filter((t) => selectedSet.has(t)).length;
                const groupAllSelected = groupSelectedCount === group.types.length;

                return (
                  <div key={group.id} className="px-2 py-1">
                    <div className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                      <span>{group.label}</span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group)}
                        className="text-xs font-medium normal-case text-foreground hover:underline"
                      >
                        {groupAllSelected ? 'Clear' : 'All'} ({groupSelectedCount}/{group.types.length})
                      </button>
                    </div>

                    <div className="space-y-1">
                      {group.types.map((type) => {
                        const isSelected = selectedSet.has(type);
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => toggleType(type)}
                            className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-gray-50"
                          >
                            <div
                              className={cn(
                                'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                                isSelected
                                  ? 'border-primary bg-white text-primary'
                                  : 'border-border bg-white text-transparent'
                              )}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </div>
                            <span className="text-foreground">
                              {ACTIVITY_TYPE_LABELS[type]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
